import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Mail, Shield, Bell, BellOff, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Profile() {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'notifications' ? 'notifications' : 'info';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [prefs, setPrefs] = useState({});
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/preferences`, { withCredentials: true });
      setPrefs(res.data.preferences || {});
      setTypes(res.data.types || []);
    } catch (e) {
      toast.error('Error al cargar preferencias');
    } finally {
      setLoading(false);
    }
  };

  const togglePref = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, { preferences: prefs }, { withCredentials: true });
      toast.success('Preferencias guardadas');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Perfil</h1>
        <p className="text-muted-foreground">Tu información personal y preferencias</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">
            <User className="w-4 h-4 mr-2" />
            Información
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notificaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Información de Usuario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-2xl">{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-semibold">{user?.name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center text-muted-foreground">
                    <User className="w-4 h-4 mr-2" />
                    Nombre
                  </Label>
                  <p className="text-base font-medium" data-testid="profile-name">{user?.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center text-muted-foreground">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Label>
                  <p className="text-base font-medium" data-testid="profile-email">{user?.email}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center text-muted-foreground">
                    <Shield className="w-4 h-4 mr-2" />
                    Rol
                  </Label>
                  <Badge variant="secondary" className="text-sm uppercase" data-testid="profile-role">
                    {user?.role}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Preferencias de Notificaciones
              </CardTitle>
              <CardDescription>
                Elegí qué eventos querés que aparezcan en tu campanita. Si desactivás un tipo, no recibirás más notificaciones de ese tipo (las viejas se mantienen).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-muted-foreground py-6">Cargando…</div>
              ) : (
                <div className="space-y-3" data-testid="notifications-preferences-list">
                  {types.map((t) => {
                    const enabled = prefs[t.key] !== false;
                    return (
                      <div
                        key={t.key}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/40 transition-colors"
                        data-testid={`pref-row-${t.key}`}
                      >
                        <div className="flex items-center gap-3">
                          {enabled ? <Bell className="w-4 h-4 text-blue-600" /> : <BellOff className="w-4 h-4 text-muted-foreground" />}
                          <div>
                            <p className="font-medium text-sm">{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.key}</p>
                          </div>
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={() => togglePref(t.key)}
                          data-testid={`pref-toggle-${t.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={saving || loading}
                  data-testid="save-notification-preferences-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando…' : 'Guardar preferencias'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Profile;
